import './App.css'
import './styles/cyberpunk.css'
import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from './components/ui/button'
import { Input } from './components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card'
import { Badge } from './components/ui/badge'
import { 
  Brain, 
  Bot, 
  Users, 
  Zap, 
  Github, 
  Star, 
  ArrowRight, 
  CheckCircle, 
  Globe, 
  Shield, 
  Code, 
  X,
  Eye,
  EyeOff,
  Terminal,
  Cpu,
  Wifi,
  Lock
} from 'lucide-react'
import logo from './assets/agentcy_one_logo_final.png'
import { AuthService } from './services/auth'

// Floating particles component
const CyberParticles = () => {
  const particles = Array.from({ length: 20 }, (_, i) => (
    <motion.div
      key={i}
      className="cyber-particle"
      initial={{ 
        x: Math.random() * window.innerWidth,
        y: window.innerHeight,
        opacity: 0 
      }}
      animate={{
        y: -100,
        opacity: [0, 1, 0],
      }}
      transition={{
        duration: Math.random() * 10 + 5,
        repeat: Infinity,
        delay: Math.random() * 5,
      }}
      style={{
        left: Math.random() * 100 + '%',
      }}
    />
  ))

  return <div className="cyber-particles">{particles}</div>
}

// Auth Modal Component
const AuthModal = ({ isOpen, onClose, onSuccess }) => {
  const [isLogin, setIsLogin] = useState(true)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      if (isLogin) {
        await AuthService.login(email, password)
      } else {
        await AuthService.register(email, password)
        // Auto login after registration
        await AuthService.login(email, password)
      }
      
      onSuccess()
      onClose()
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <motion.div
        className="modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="modal-content"
          initial={{ scale: 0.7, opacity: 0, rotateX: -15, y: 100 }}
          animate={{ scale: 1, opacity: 1, rotateX: 0, y: 0 }}
          exit={{ scale: 0.7, opacity: 0, rotateX: 15, y: -100 }}
          transition={{ 
            type: "spring", 
            damping: 20, 
            stiffness: 200,
            duration: 0.6
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="cyber-modal p-8 floating pulse-glow">
            <div className="relative">
              <button
                className="absolute right-0 top-0 text-cyan-400 hover:text-cyan-300 hover:rotate-90 transition-all duration-300 z-10 p-2"
                onClick={onClose}
              >
                <X className="h-5 w-5" />
              </button>
              
              <motion.div 
                className="text-center mb-8"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <h2 className="text-3xl font-bold holographic mb-4 flex items-center justify-center">
                  <Terminal className="mr-3 h-8 w-8" />
                  {isLogin ? 'NEURAL INTERFACE' : 'SYSTEM INITIALIZATION'}
                </h2>
                <p className="text-cyan-300/70 text-sm uppercase tracking-wider">
                  {isLogin ? 'Establish Connection' : 'Create New Identity'}
                </p>
              </motion.div>
              {error && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="mt-6 text-red-400 text-sm text-center bg-red-900/30 p-4 rounded-lg border border-red-500/30"
                >
                  <span className="uppercase tracking-wide font-bold">SYSTEM ERROR: {error}</span>
                </motion.div>
              )}
              
              <motion.form 
                onSubmit={handleSubmit} 
                className="space-y-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                <motion.div 
                  className="space-y-2"
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  <label className="text-sm font-bold text-cyan-300 uppercase tracking-wider">Network Address</label>
                  <input
                    type="email"
                    placeholder="NEURAL.LINK@AGENTCY.ONE"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full cyber-input-3d"
                    required
                  />
                </motion.div>
                
                <motion.div 
                  className="space-y-2"
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.6 }}
                >
                  <label className="text-sm font-bold text-cyan-300 uppercase tracking-wider">Security Key</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="ENTER AUTHORIZATION CODE"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full cyber-input-3d pr-12"
                      required
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-cyan-400 hover:text-cyan-300 transition-colors duration-300"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </motion.div>

                <motion.button
                  type="submit"
                  disabled={loading}
                  className="w-full cyber-button-3d mt-8"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                >
                  {loading ? (
                    <motion.div
                      className="flex items-center justify-center"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    >
                      <Cpu className="h-5 w-5 mr-3" />
                      CONNECTING...
                    </motion.div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <Lock className="h-5 w-5 mr-3" />
                      {isLogin ? 'JACK IN' : 'INITIALIZE'}
                    </div>
                  )}
                </motion.button>
              </motion.form>

              <motion.div 
                className="text-center mt-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.9 }}
              >
                <button
                  className="text-cyan-400/70 hover:text-cyan-300 text-sm uppercase tracking-wider transition-colors duration-300 font-medium"
                  onClick={() => setIsLogin(!isLogin)}
                >
                  {isLogin 
                    ? "NEW USER? INITIALIZE PROFILE" 
                    : "RETURNING USER? ACCESS TERMINAL"
                  }
                </button>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

// Dashboard Component
const Dashboard = ({ user, onLogout }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-slate-900 relative overflow-hidden"
    >
      <CyberParticles />
      <div className="cyber-grid-bg"></div>
      
      {/* Dashboard Header */}
      <header className="floating-header">
        <motion.div
          className="cyber-glass cyber-glass-hover p-4 rounded-2xl"
          initial={{ y: -100 }}
          animate={{ y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img src={logo} alt="Agentcy.one" className="h-10 w-10" />
              <div>
                <span className="text-xl font-bold neon-blue">AGENTCY.ONE</span>
                <p className="text-xs text-cyan-300/70">Neural Network Interface</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                <Wifi className="h-3 w-3 mr-1" />
                CONNECTED
              </Badge>
              <span className="text-sm text-cyan-300">Welcome, {user?.email}</span>
              <Button 
                onClick={onLogout}
                className="cyber-button-secondary"
                size="sm"
              >
                LOGOUT
              </Button>
            </div>
          </div>
        </motion.div>
      </header>

      {/* Dashboard Content */}
      <div className="pt-32 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              <span className="neon-blue">AI CONTROL</span>{' '}
              <span className="neon-pink">CENTER</span>
            </h1>
            <p className="text-xl text-cyan-300/80 max-w-2xl mx-auto">
              Neural network management interface for autonomous agent orchestration
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Bot, title: 'AI AGENTS', count: '0', color: 'neon-blue' },
              { icon: Zap, title: 'ACTIVE TASKS', count: '0', color: 'neon-yellow' },
              { icon: Shield, title: 'SECURITY STATUS', count: 'SECURE', color: 'neon-green' }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + i * 0.1 }}
              >
                <Card className="cyber-glass cyber-glass-hover p-6 text-center">
                  <item.icon className={`h-12 w-12 mx-auto mb-4 ${item.color}`} />
                  <h3 className="text-lg font-semibold text-cyan-300 mb-2">{item.title}</h3>
                  <p className={`text-3xl font-bold ${item.color}`}>{item.count}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  )
}

// Main App Component
function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState(null)
  const [showAuthModal, setShowAuthModal] = useState(false)

  useEffect(() => {
    if (AuthService.isAuthenticated()) {
      setIsAuthenticated(true)
      setUser(AuthService.getUser())
    }
  }, [])

  const handleAuthSuccess = () => {
    setIsAuthenticated(true)
    setUser(AuthService.getUser())
  }

  const handleLogout = () => {
    AuthService.logout()
    setIsAuthenticated(false)
    setUser(null)
  }

  // If authenticated, show dashboard
  if (isAuthenticated) {
    return <Dashboard user={user} onLogout={handleLogout} />
  }

  // Landing page
  return (
    <div className="min-h-screen bg-slate-900 relative overflow-hidden">
      <CyberParticles />
      <div className="cyber-grid-bg"></div>
      
      {/* Floating Header */}
      <header className="floating-header">
        <motion.div
          className="cyber-glass cyber-glass-hover p-4 rounded-2xl"
          initial={{ y: -100 }}
          animate={{ y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center justify-between">
            <motion.div 
              className="flex items-center space-x-3"
              whileHover={{ scale: 1.05 }}
            >
              <img src={logo} alt="Agentcy.one" className="h-10 w-10" />
              <div>
                <span className="text-xl font-bold neon-blue">AGENTCY.ONE</span>
                <p className="text-xs text-cyan-300/70">Neural Interface</p>
              </div>
            </motion.div>
            <div className="flex items-center space-x-4">
              <motion.a
                href="https://github.com"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                className="cyber-button flex items-center space-x-2"
              >
                <Github className="h-4 w-4" />
                <span>GITHUB</span>
              </motion.a>
              <motion.button
                onClick={() => setShowAuthModal(true)}
                whileHover={{ scale: 1.05 }}
                className="cyber-button-secondary flex items-center space-x-2"
              >
                <Terminal className="h-4 w-4" />
                <span>ACCESS</span>
              </motion.button>
            </div>
          </div>
        </motion.div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Badge className="mb-6 bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
              <Star className="h-3 w-3 mr-1" />
              FREE NEURAL NETWORK
            </Badge>
            <motion.h1 
              className="text-5xl md:text-7xl font-bold mb-8 leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <span className="neon-blue">AI AGENT</span>{' '}
              <span className="block neon-pink">ORCHESTRATION</span>
            </motion.h1>
            <motion.p 
              className="text-xl text-cyan-300/80 mb-12 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
            >
              Deploy autonomous AI agents with neural browser automation, 
              human-machine interfaces, and quantum orchestration protocols.
            </motion.p>
            
            <motion.div 
              className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
            >
              <motion.button
                onClick={() => setShowAuthModal(true)}
                className="cyber-button text-lg px-8 py-4 flex items-center space-x-3"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Brain className="h-5 w-5" />
                <span>INITIALIZE SYSTEM</span>
                <ArrowRight className="h-5 w-5" />
              </motion.button>
              <motion.a
                href="https://github.com"
                target="_blank"
                rel="noopener noreferrer"
                className="cyber-button-secondary text-lg px-8 py-4 flex items-center space-x-3"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Code className="h-5 w-5" />
                <span>VIEW SOURCE</span>
              </motion.a>
            </motion.div>
          </motion.div>

          {/* Features Grid */}
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-20"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2 }}
          >
            {[
              {
                icon: Bot,
                title: 'NEURAL AGENTS',
                desc: 'Deploy autonomous AI entities with quantum decision matrices',
                color: 'neon-blue'
              },
              {
                icon: Shield,
                title: 'QUANTUM SECURITY',
                desc: 'Military-grade encryption with neural firewall protection',
                color: 'neon-green'
              },
              {
                icon: Zap,
                title: 'INSTANT DEPLOYMENT',
                desc: 'Zero-latency agent provisioning on global edge networks',
                color: 'neon-yellow'
              }
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.4 + i * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <Card className="cyber-glass cyber-glass-hover p-8 h-full">
                  <feature.icon className={`h-12 w-12 mb-6 ${feature.color}`} />
                  <h3 className="text-xl font-bold mb-4 text-cyan-100">{feature.title}</h3>
                  <p className="text-cyan-300/70">{feature.desc}</p>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
      />
    </div>
  )
}

export default App